import {
  fork,
  take,
  takeEvery,
  all,
  put,
  call,
  select
} from "redux-saga/effects";
import moment from "moment-timezone";

import { actions, getters } from "./model";
import { SIDEBAR_INSTANCE_ID, FLYOVER_SESSION_FORM_ID } from "./constants";
import { apiCall } from "App/Data/sagas";
import { actions as sliderSidebarActions } from "ui-kit/SliderSidebar/model";
import { getCredentials } from "redux/modules/user/selectors";
import { eventDetails as getPortalEventDetails } from "redux/modules/portal/selectors";
import { registerError } from "redux/modules/errors/actions";
import { showSnackbar } from "redux/modules/snackbar/actions";
import * as R from "ramda";
const {
  ACCOUNTS,
  CONTACTS,
  SESSIONS
} = require("@sessionboard/sessionboard-value-types/src/constants/standard-module-field-ids");
import { getEditedField, getSessionStartsAtField } from "./selectors";
import { setOtherZone, setLocalZone } from "utils/General";

import { VIEW_TYPES } from "./View/Files/constants";
import { DATE_FORMAT } from "Portal/NewPortal/Sections/Profile/constants";

import { actions as FormActions } from "ui-kit/Form/model";

import { actions as filesActions } from "./View/Files/model";

import { decorateValueToSave } from "Portal/NewPortal/utils";
import { getFieldValueToShow } from "../../Profile/selectors";

const init = function*() {
  yield put(actions.resetData());
};

const getParams = function*() {
  const credentials = yield select(getCredentials);
  const eventDetails = yield select(getPortalEventDetails);

  return {
    credentials,
    eventDetails
  };
};

const initEditSessionMode = function*() {
  let sessionData = yield select(getters.sessionData);
  const data = sessionData.fields.reduce((a, b) => {
    a[b.id] = b.editable_value;
    return a;
  }, {});
  yield put(actions.setData(data));
  yield put(actions.setIsEditingSession(true));
};

const fieldUpdate = function*({ id, fieldId }) {
  const { eventDetails } = yield call(getParams);

  const newValue = yield select(getEditedField, {
    instanceId: id,
    fieldId
  });

  const processedValue = R.propOr(newValue, fieldId, {
    [CONTACTS.AVATAR_URL]: R.pathOr(null, [0, "url"], newValue),
    [ACCOUNTS.BANNER_URL]: R.pathOr(null, [0, "url"], newValue),
    [ACCOUNTS.LOGO_URL]: R.pathOr(null, [0, "url"], newValue),
    [SESSIONS.STARTS_AT]: newValue
      ? moment
          .tz(
            moment(
              setOtherZone(newValue, R.prop("timezone", eventDetails))
            ).toDate(),
            DATE_FORMAT,
            R.prop("timezone", eventDetails)
          )
          .utc()
          .format()
      : null,
    [SESSIONS.ENDS_AT]: newValue
      ? moment
          .tz(
            moment(
              setOtherZone(newValue, R.prop("timezone", eventDetails))
            ).toDate(),
            DATE_FORMAT,
            R.prop("timezone", eventDetails)
          )
          .utc()
          .format()
      : null
  });

  if (fieldId === SESSIONS.STARTS_AT) {
    // handle: start date
    const data = yield select(getters.data);
    const updatedFieldValues = yield select(getters.updatedFieldValues);
    const start = moment(processedValue);
    const endValue = getFieldValueToShow({
      data,
      updatedData: updatedFieldValues,
      fieldId: SESSIONS.ENDS_AT
    });
    let end = moment(endValue);

    if (R.isNil(endValue) || start.isAfter(end)) {
      end = start
        .clone()
        .add(1, "hours")
        .utc()
        .format();
      const endDate = moment(end);
      const val = endDate.isValid() && newValue ? end : null;
      if (val) {
        yield all([
          put(
            actions.setUpdatedFieldValue({
              fieldId: SESSIONS.ENDS_AT,
              value: val
            })
          ),
          put(
            FormActions.setFieldValue(
              setLocalZone(val, R.prop("timezone", eventDetails), true),
              {
                meta: {
                  instanceId: FLYOVER_SESSION_FORM_ID,
                  fieldId: SESSIONS.ENDS_AT
                }
              }
            )
          )
        ]);
      }
    }
  } else if (fieldId === SESSIONS.ENDS_AT) {
    // handle: end date
    const data = yield select(getters.data);
    const updatedFieldValues = yield select(getters.updatedFieldValues);
    const startValue = getFieldValueToShow({
      data,
      updatedData: updatedFieldValues,
      fieldId: SESSIONS.STARTS_AT
    });

    const startsAtField = yield select(getSessionStartsAtField);
    const end = moment(processedValue);
    let start = moment(startValue);

    if (!startsAtField) {
      yield put(
        actions.setFieldsErrorMessage(
          "Start date is required to be visible to edit end date"
        )
      );
      return;
    }

    if (startsAtField && startsAtField.is_locked && end.isBefore(start)) {
      yield put(
        actions.setFieldsErrorMessage("End date must be after the start date")
      );
      return;
    }

    if (R.isNil(startValue) || end.isBefore(start)) {
      start = end
        .clone()
        .subtract(1, "hours")
        .utc()
        .format();
      const startDate = moment(start);
      const val = startDate.isValid() && newValue ? start : null;
      if (val) {
        yield all([
          put(
            actions.setUpdatedFieldValue({
              fieldId: SESSIONS.STARTS_AT,
              value: val
            })
          ),
          put(
            FormActions.setFieldValue(
              setLocalZone(val, R.prop("timezone", eventDetails), true),
              {
                meta: {
                  instanceId: FLYOVER_SESSION_FORM_ID,
                  fieldId: SESSIONS.STARTS_AT
                }
              }
            )
          )
        ]);
      }
    }
  }

  // handle: other fields
  yield all([
    put(
      actions.setUpdatedFieldValue({
        fieldId,
        value: processedValue
      })
    ),
    put(actions.setFieldsErrorMessage(null))
  ]);
};

const watchUpdateFields = function*() {
  for (;;) {
    const {
      meta: { instanceId, fieldId }
    } = yield take(FormActions.setFieldValue.type);
    if (instanceId === FLYOVER_SESSION_FORM_ID) {
      yield call(fieldUpdate, {
        id: instanceId,
        fieldId
      });
    }
  }
};

const resetEditData = function*() {
  yield all([
    put(actions.setSaving(false)),
    put(actions.setTransitionToTabIdAfterSave(null)),
    put(actions.setUpdatedFieldValues({})),
    put(actions.setHasUnSavedChangesModal(false)),
    put(actions.setIsEditingSession(false))
  ]);
};

const save = function*(
  { payload: { viaUnsavedChangesModal } } = {
    payload: { viaUnsavedChangesModal: false }
  }
) {
  try {
    yield put(actions.setSaving(true));

    const sessionData = yield select(getters.sessionData);
    const fields = sessionData.fields;
    const updatedFieldValues = yield select(getters.updatedFieldValues);
    const lockedFieldIds = sessionData.locked_field_ids;
    const { eventDetails } = yield call(getParams);
    const transitionToTabIdAfterSave = yield select(
      getters.transitionToTabIdAfterSave
    );

    if (sessionData.isSubsession) {
      // if the parent session does not have a time slot, set the error
      if (
        (!sessionData.parentSessionStartsAt ||
          !sessionData.parentSessionEndsAt) &&
        (updatedFieldValues[SESSIONS.STARTS_AT] ||
          updatedFieldValues[SESSIONS.ENDS_AT])
      ) {
        yield put(
          actions.setFieldsErrorMessage(
            "The parent session does not have a time slot so the subsession cannot have one"
          )
        );
        yield put(actions.setSaving(false));
        return;
      }

      // if the start time is before the parent session starts, set the error
      if (
        updatedFieldValues[SESSIONS.STARTS_AT] &&
        moment(updatedFieldValues[SESSIONS.STARTS_AT])
          .seconds(0)
          .milliseconds(0)
          .isBefore(
            moment(sessionData.parentSessionStartsAt)
              .seconds(0)
              .milliseconds(0)
          )
      ) {
        yield put(
          actions.setFieldsErrorMessage(
            `The start time should be after the parent session starts (${moment(
              sessionData.parentSessionStartsAt
            )
              .tz(R.prop("timezone", eventDetails))
              .format("MMM D, h:mm A")})`
          )
        );
        yield put(actions.setSaving(false));
        return;
      }

      // if the end time is after the parent session ends, set the error
      if (
        updatedFieldValues[SESSIONS.ENDS_AT] &&
        moment(updatedFieldValues[SESSIONS.ENDS_AT])
          .seconds(0)
          .milliseconds(0)
          .isAfter(
            moment(sessionData.parentSessionEndsAt)
              .seconds(0)
              .milliseconds(0)
          )
      ) {
        yield put(
          actions.setFieldsErrorMessage(
            `The end time should be before the parent session ends (${moment(
              sessionData.parentSessionEndsAt
            )
              .tz(R.prop("timezone", eventDetails))
              .format("MMM D, h:mm A")})`
          )
        );
        yield put(actions.setSaving(false));
        return;
      }
    }

    // decorate values to send to backend
    const formattedValues = Object.keys(updatedFieldValues).reduce(
      (a, fieldId) => {
        const field = fields.find(f => f.id === fieldId);
        const isLockedField = lockedFieldIds.includes(fieldId);

        if (!isLockedField && field) {
          const value = updatedFieldValues[fieldId];
          a.push(decorateValueToSave({ fieldId, field, value }));
        }
        return a;
      },
      []
    );

    // save data
    yield call(apiCall, {
      method: "post",
      url: `/portal/event/${eventDetails.uuid}/session-content/${sessionData.id}/session`,
      data: {
        values: formattedValues
      }
    });

    // show save confirmation
    yield put(showSnackbar({ message: "Details Saved" }));

    yield put(actions.setLoading(true));

    // eslint-disable-next-line no-use-before-define
    yield call(refreshSessionData, sessionData.id);

    // show new tab that the user originally was intending to go to
    if (viaUnsavedChangesModal && transitionToTabIdAfterSave) {
      yield put(actions.setSelectedTab(transitionToTabIdAfterSave));
    }

    yield call(resetEditData);

    yield put(actions.setLoading(false));

    yield put(actions.refreshData());
  } catch (error) {
    yield put(actions.setLoading(false));
    yield put(actions.setIsEditingSession(false));
    yield put(actions.setSaving(false));
    yield put(
      registerError([
        {
          system: error,
          user: "An error occurred while saving details"
        }
      ])
    );
  }
};

const cancelSave = function*() {
  const sessionData = yield select(getters.sessionData);

  yield put(actions.setLoading(true));

  // eslint-disable-next-line no-use-before-define
  yield call(refreshSessionData, sessionData.id);

  yield call(resetEditData);

  yield put(actions.setLoading(false));
};

const refreshSessionData = function*(sessionId) {
  const { eventDetails } = yield call(getParams);

  const [{ payload: sessionData }, { payload: contentSettings }] = yield all([
    call(apiCall, {
      method: "get",
      url: `/portal/event/${eventDetails.uuid}/session-content/${sessionId}/session`
    }),
    call(apiCall, {
      method: "get",
      url: `/portal/event/${eventDetails.uuid}/session-content/settings`
    })
  ]);

  yield put(
    actions.setInitialData({
      sessionData,
      contentSettings
    })
  );
};

const openSessionFlyover = function*({
  payload: { sessionData, fromHomePage = false, initTab = null }
}) {
  yield put(actions.resetData());

  if (fromHomePage) {
    yield put(actions.setFromHomePage(true));
  }

  yield put(
    sliderSidebarActions.setIsDrawerOpen(true, {
      meta: { instanceId: SIDEBAR_INSTANCE_ID }
    })
  );

  try {
    yield put(actions.setLoading(true));

    yield call(refreshSessionData, sessionData.id);

    if (initTab) {
      yield put(actions.setSelectedTab(initTab));
    }
  } catch (error) {
    yield put(
      registerError([
        {
          system: error,
          user: "An error occurred while loading session"
        }
      ])
    );
  } finally {
    yield put(actions.setLoading(false));
  }
};

const closeSessionFlyover = function*() {
  yield put(
    sliderSidebarActions.setIsDrawerOpen(false, {
      meta: { instanceId: SIDEBAR_INSTANCE_ID }
    })
  );
};

const openSession = function*() {
  const session = yield select(getters.sessionData);
  window.location = session.url;
  return true;
};

const showUploadFilePicker = function*() {
  yield put(filesActions.setView(VIEW_TYPES.FILE));
};

const getParticipantData = function*() {};

const onCancelUnsavedChanges = function*() {
  yield put(actions.setHasUnSavedChangesModal(false));
  yield call(resetEditData);
};

const onConfirmUnsavedChanges = function*() {
  yield call(resetEditData);
};

//

const watchInit = function*() {
  yield takeEvery(actions.init.type, init);
};

const watchOpenSessionFlyover = function*() {
  yield takeEvery(actions.openSessionFlyover.type, openSessionFlyover);
};

const watchCloseSessionFlyover = function*() {
  yield takeEvery(actions.closeSessionFlyover.type, closeSessionFlyover);
};

const watchInitSessionEditMode = function*() {
  yield takeEvery(actions.enableSessionEditMode.type, initEditSessionMode);
};

const watchOpenSession = function*() {
  yield takeEvery(actions.openSession.type, openSession);
};

const watchShowUploadFilePicker = function*() {
  yield takeEvery(actions.showUploadFilePicker.type, showUploadFilePicker);
};

const watchSelectParticipant = function*() {
  yield takeEvery(actions.setContactDataInUse.type, getParticipantData);
};

const watchSave = function*() {
  yield takeEvery(actions.save.type, save);
};
const watchCancel = function*() {
  yield takeEvery(actions.cancel.type, cancelSave);
};
const watchOnCancelUnsavedChanges = function*() {
  yield takeEvery(actions.onCancelUnsavedChanges.type, onCancelUnsavedChanges);
};
const watchOnConfirmUnsavedChanges = function*() {
  yield takeEvery(
    actions.onConfirmUnsavedChanges.type,
    onConfirmUnsavedChanges
  );
};

const rootSaga = function*() {
  yield all([
    fork(watchInit),
    fork(watchUpdateFields),
    fork(watchInitSessionEditMode),
    fork(watchOpenSession),
    fork(watchOpenSessionFlyover),
    fork(watchCloseSessionFlyover),
    fork(watchShowUploadFilePicker),
    fork(watchSelectParticipant),
    fork(watchSave),
    fork(watchCancel),
    fork(watchUpdateFields),
    fork(watchOnCancelUnsavedChanges),
    fork(watchOnConfirmUnsavedChanges)
  ]);
};
export default rootSaga;
